// ./src/components/features/Inventory/InventoryItemsFilterBar/InventoryItemsFilterBar.tsx
//
// Filter controls for the Inventory-items catalog: by category and a "tracks low
// stock" toggle. Fully controlled — the page owns the value and maps it to an
// InventoryItemFilter. (The backend has no server `lowStock` filter, so low-stock is
// narrowed via the trackLowStock flag + search, not a dedicated facet.)

import React from "react";
import { Group, Box, Stack, Button, Switch, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, CategorySelect } from "@/components/generic";

export interface InventoryItemsFilterValue {
  categoryId: string | null;
  trackLowStock: boolean;
}

export interface InventoryItemsFilterBarProps {
  value: InventoryItemsFilterValue;
  onChange: (value: InventoryItemsFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
  vertical?: boolean;
}

export const EMPTY_INVENTORY_ITEMS_FILTER: InventoryItemsFilterValue = {
  categoryId: null,
  trackLowStock: false,
};

const isFilterActive = (value: InventoryItemsFilterValue): boolean =>
  Boolean(value.categoryId) || value.trackLowStock;

const CATEGORY_STYLE: React.CSSProperties = { flex: "1 1 220px", minWidth: 200 };

const InventoryItemsFilterBarComponent: React.FC<InventoryItemsFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
  vertical = false,
}) => {
  const { t } = useTranslation(["inventory", "common"]);

  const active = isFilterActive(value);

  const categoryControl = (
    <Box style={vertical ? undefined : CATEGORY_STYLE}>
      <CategorySelect
        group="inventory"
        label={t("inventory:filters.category.label", { defaultValue: "Category" })}
        placeholder={t("inventory:filters.category.placeholder", { defaultValue: "All categories" })}
        value={value.categoryId}
        onChange={(id) => onChange({ ...value, categoryId: id })}
        clearable
        disabled={disabled}
        size="md"
      />
    </Box>
  );

  const switchControl = (
    <Switch
      label={t("inventory:filters.trackLowStock.label", { defaultValue: "Low-stock tracked only" })}
      checked={value.trackLowStock}
      onChange={(e) => onChange({ ...value, trackLowStock: e.currentTarget.checked })}
      disabled={disabled}
      size="md"
    />
  );

  // Desktop: reserve a (hidden) label row so the switch's input row lines up with the
  // Category select's input — the select's own label would otherwise float it high.
  const lowStockControl = (
    <Box>
      <Text size="md" fw={500} mb={4} aria-hidden style={{ visibility: "hidden" }}>
        &nbsp;
      </Text>
      <Box style={{ display: "flex", alignItems: "center", minHeight: 42 }}>{switchControl}</Box>
    </Box>
  );

  if (vertical) {
    return (
      <Stack gap="md">
        {categoryControl}
        {switchControl}
      </Stack>
    );
  }

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      {categoryControl}
      {lowStockControl}

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_INVENTORY_ITEMS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const InventoryItemsFilterBar = React.memo(InventoryItemsFilterBarComponent);
